import React, { useEffect, useState } from 'react';

import { Box, COLORS, Text } from '@clutter/clean';

import {
  Moving__MaterialPackageSetEntryFragment,
  Moving__WhiteGloveBundleEnum,
  useMovingMaterialPackageSetEntriesFetchMutation,
} from '@graphql/platform';

import { useMovingCheckoutContext } from '@root/components/checkout/context';
import { MovingCheckoutStep } from '@root/components/checkout/types';
import { scrollToStep } from '@root/components/checkout/utilities/scroll_animation';
import { useTrack } from '@root/initializers/wt';
import { EventSchema } from '@root/resources/wt/types';

import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { useMountedRef } from '@utils/hooks/mount';
import { useBreakpoints } from '@utils/hooks/use_breakpoints';

import { Card } from './card';
import { ComparisonModal } from './comparison_modal';
import {
  basicBundleValueProps,
  fullServiceBundleValueProps,
} from './value_props';

export const Quote: React.FC = () => {
  const {
    flowState: {
      values: { movingQuote, movingBundle, leadToken },
      onChange,
    },
    createQuote,
  } = useMovingCheckoutContext();
  const [highlightedBundle, setHighlightedBundle] = useState(movingBundle);
  const [showModal, setShowModal] = useState(false);
  const [packageSetEntries, setPackageSetEntries] =
    useState<ReadonlyArray<Moving__MaterialPackageSetEntryFragment>>();

  const [fetchPackingMaterials] =
    useMovingMaterialPackageSetEntriesFetchMutation();

  const mountedRef = useMountedRef();
  const trackFunnelEvent = useTrackFunnelEvents();

  const track = useTrack({
    action: 'click',
  });

  useEffect(() => {
    if (!movingQuote) return;

    fetchPackingMaterials({
      variables: { signedQuoteID: movingQuote.signedID },
    }).then(({ data }) => {
      if (!mountedRef.current) return;
      if (data?.materialPackageSetEntriesFetch) {
        setPackageSetEntries(
          data?.materialPackageSetEntriesFetch?.materialPackageSetEntries,
        );
      } else {
        throw new Error('Failed to fetch material package set entries');
      }
    });
  }, [movingQuote, fetchPackingMaterials, mountedRef]);

  const { isMobile } = useBreakpoints();
  const onCompare = (bundle: Moving__WhiteGloveBundleEnum) => {
    track({
      container: `${bundle} card`,
      objectType: 'button',
      label: 'Compare',
    });

    setHighlightedBundle(bundle);
    setShowModal(true);
  };

  const onMatrixSelectBundle = (bundle: Moving__WhiteGloveBundleEnum) => {
    onSelectBundle(bundle, 'comparison_matrix');
    setShowModal(false);
  };

  const hasEnteredLeadInformation = !!leadToken;
  const whiteGloveMoverCountOptions = movingQuote?.whiteGloveMoverCountOptions;
  const flatRateOptions = movingQuote?.flatRateOptions;
  const flatRateEligible = !!flatRateOptions?.length;
  const options = flatRateEligible
    ? flatRateOptions
    : whiteGloveMoverCountOptions;
  const recommendedMoverCount = movingQuote?.fullServiceMoverCount;
  const minimumHours = movingQuote?.movingConfiguration
    ? movingQuote?.movingConfiguration?.requiredLaborDuration / 3600
    : 0;

  if (!options?.length || options.length < 3) {
    return null;
  }

  const basicBundleOption = options[0];
  const fullServiceBundleOption = options[2];

  const basicBundleFlatRate = basicBundleOption.flatRateDetails?.totalAmount;
  const fullServiceBundleFlatRate =
    fullServiceBundleOption.flatRateDetails?.totalAmount;

  const basicBundleLaborRate = basicBundleOption.laborRate?.amount || 0;
  const fullServiceBundleLaborRate =
    fullServiceBundleOption.laborRate?.amount || 0;

  const unlimitedPackingPackage = packageSetEntries?.find(
    (entry) => entry.package.name === 'Unlimited Packing Supplies',
  );

  const onSelectBundle = (
    bundle: Moving__WhiteGloveBundleEnum,
    container = 'card',
  ) => {
    let moverCount;

    if (bundle === Moving__WhiteGloveBundleEnum.Basic) {
      onChange('movingBundle', Moving__WhiteGloveBundleEnum.Basic);
      onChange('packingMaterials', false);
      moverCount = basicBundleOption.moverCount;
    } else {
      onChange('movingBundle', Moving__WhiteGloveBundleEnum.FullService);
      onChange(
        'packingMaterials',
        movingQuote?.longDistance ? undefined : unlimitedPackingPackage,
      );
      moverCount = fullServiceBundleOption.moverCount;
    }

    track({
      container: container,
      objectType: 'button',
      label: 'Select',
      value: bundle,
    });

    trackFunnelEvent({
      schema: EventSchema.WWW__MovePriceCompleted,
      action: 'click',
      metadata: {},
    });

    onChange('moverCountSelected', true);
    onChange(
      'adjustedMoverCount',
      moverCount === recommendedMoverCount ? undefined : moverCount,
      () => createQuote(),
    );

    scrollToStep(MovingCheckoutStep.StoragePlanAttach, 'top');
  };

  return (
    <Box margin={['48px 0 0', null, '72px 0 0']}>
      <Box margin="0 0 16px">
        <Text.Title size="small" color={COLORS.tealDark}>
          Select your package
        </Text.Title>
        <Text.Body color={COLORS.storm}>
          Our Move & Pack Specialists are trained for the safe packing of boxes
          and furniture.
        </Text.Body>
      </Box>
      {highlightedBundle && (
        <ComparisonModal
          selectedBundle={highlightedBundle}
          isOpen={showModal}
          handleModalClose={() => setShowModal(false)}
          onSelectBundle={onMatrixSelectBundle}
        />
      )}
      <Box.Flex flexDirection={['column', null, null, 'row']} gap="12px">
        <Card
          testId="basicBundle"
          bannerValue="Lowest cost"
          title="Standard"
          subtitle="You do the packing, we move your packed boxes and furniture."
          selected={movingBundle === Moving__WhiteGloveBundleEnum.Basic}
          showPrice={hasEnteredLeadInformation}
          flatRate={basicBundleFlatRate}
          laborRate={basicBundleLaborRate}
          minimumHours={minimumHours}
          valueProps={basicBundleValueProps(isMobile)}
          onCompare={() => onCompare(Moving__WhiteGloveBundleEnum.Basic)}
          onSelect={() => onSelectBundle(Moving__WhiteGloveBundleEnum.Basic)}
        />
        <Card
          testId="fullServiceBundle"
          bannerValue="Best value"
          title="Premium"
          subtitle="We bring boxes and packing materials, pack and unpack as much as you need us to!"
          selected={movingBundle === Moving__WhiteGloveBundleEnum.FullService}
          showPrice={hasEnteredLeadInformation}
          flatRate={fullServiceBundleFlatRate}
          laborRate={fullServiceBundleLaborRate}
          materialsPackage={
            movingQuote?.longDistance ? undefined : unlimitedPackingPackage
          }
          minimumHours={minimumHours}
          showBadge={false}
          valueProps={fullServiceBundleValueProps(isMobile)}
          onCompare={() => onCompare(Moving__WhiteGloveBundleEnum.FullService)}
          onSelect={() =>
            onSelectBundle(Moving__WhiteGloveBundleEnum.FullService)
          }
        />
      </Box.Flex>
    </Box>
  );
};
